<template class="main-events-timeline">
    <a-spin
        v-if="isLoading"
        spinning
        :indicator="loadingIndicator"
        tip="Загрузка..."
    />

    <a-empty
        v-else-if="!historyData.length"
        :image="emptyImage"
    >
        <template #description>
            История лечения отсутствует.
        </template>
    </a-empty>

    <div
        v-else
        ref="historyScrollEl"
        class="treatment-history__scroll"
    >
        <a-tree
            v-model:selectedKeys="historySelectedKeys"
            v-model:expandedKeys="expandedKeys"
            :tree-data="historyData"
            show-icon
            class="events-timeline"
            @select="onHistoryItemSelect"
        >
            <template #title="item">
                <span
                    :data-history-key="getNodeKey(item)"
                    :data-test-id="`treatment-entry-${item.pos}`"
                    class="treatment-history__title"
                >
                    <profile-two-tone
                        v-if="getEventDescription(item.dataRef.entityName.toUpperCase())
                            .icon === 'profile'"
                        :two-tone-color="getEventDescription(
                            item.dataRef.entityName.toUpperCase()
                        ).color"
                        style="font-size: 16px;"
                    />
                    <template
                        v-if="getEventDescription(item.dataRef.entityName.toUpperCase())
                            .icon === 'medicine-box'"
                    >
                        <a-tooltip v-if="item.dataRef.isReadMode">
                            <template #title>
                                {{ item.dataRef.userNameReadMode
                                    ? 'С данным протоколом работает '
                                        + item.dataRef.userNameReadMode
                                    : 'С данным протоколом работает врач.'
                                }}
                            </template>
                            <medicine-box-two-tone
                                :two-tone-color="'#ffbc4b'"
                                style="font-size: 16px;"
                            />
                        </a-tooltip>
                        <medicine-box-two-tone
                            v-else
                            :two-tone-color="getEventDescription(
                                item.dataRef.entityName.toUpperCase()
                            ).color"
                            style="font-size: 16px;"
                        />
                    </template>
                    <experiment-two-tone
                        v-if="getEventDescription(item.dataRef.entityName.toUpperCase())
                            .icon === 'experiment'"
                        :two-tone-color="getEventDescription(
                            item.dataRef.entityName.toUpperCase()
                        ).color"
                        style="font-size: 16px;"
                    />
                    <phone-two-tone
                        v-if="getEventDescription(item.dataRef.entityName.toUpperCase())
                            .icon === 'phone'"
                        :two-tone-color="getEventDescription(
                            item.dataRef.entityName.toUpperCase()
                        ).color"
                        style="font-size: 16px;"
                    />
                </span>&nbsp;
                <template v-if="!item.isChild">
                    {{ item.eventDate
                        ? $moment(item.eventDate, 'YYYY-MM-DD').format('DD.MM.YYYY')
                        : 'Дата отсутствует'
                    }}
                    -
                    <span v-if="!patientId">
                        {{ item.patientName }}.
                    </span>
                    {{ item.eventName }}
                </template>
                <strong v-if="item.eventInfo">
                    "{{ item.eventInfo }}"
                </strong>
            </template>
        </a-tree>
    </div>
</template>

<script>
import { createVNode } from 'vue';
import axios from 'axios';
import ProfileTwoTone from '@ant-design/icons-vue/ProfileTwoTone';
import MedicineBoxTwoTone from '@ant-design/icons-vue/MedicineBoxTwoTone';
import ExperimentTwoTone from '@ant-design/icons-vue/ExperimentTwoTone';
import PhoneTwoTone from '@ant-design/icons-vue/PhoneTwoTone';
import {
    Empty as AEmpty,
} from 'ant-design-vue';
import LoadingSpinner from '@/utils/Spinner.vue';
import { PAGE_NAMES } from '@/composables/useBackState';
import { savePageBackState } from '@/utils/backNavigation';
import { showErrorNotification } from '@/utils';

export default {
    components: {
        AEmpty,
        ProfileTwoTone,
        MedicineBoxTwoTone,
        ExperimentTwoTone,
        PhoneTwoTone,
    },
    props: {
        patientId: {
            type: Number,
            default: null,
        },
        onlyStaffPatients: {
            type: Boolean,
            default: false,
        },
        backNavigationContext: {
            type: Object,
            default: null,
        },
        restoreState: {
            type: Object,
            default: null,
        },
        getPatientListScrollTop: {
            type: Function,
            default: null,
        },
        sourcePageName: {
            type: String,
            default: 'main',
        },
    },
    emits: ['restored'],
    data() {
        return {
            isLoading: false,
            historyRequestAbortSource: undefined,
            historyData: [],
            expandedKeys: [],
            highlightedNodeKey: null,
            emptyImage: AEmpty.PRESENTED_IMAGE_SIMPLE,
            loadingIndicator: createVNode(LoadingSpinner),
        };
    },
    computed: {
        historySelectedKeys: {
            get() {
                return [];
            },
            set() {},
        },
    },
    watch: {
        patientId(val) {
            this.getTreatmentHistory(val);
        },
        onlyStaffPatients() {
            this.getTreatmentHistory(this.patientId);
        },
        restoreState(val) {
            if (val && this.historyData.length && !this.isLoading) {
                this.applyRestoreState(val);
            }
        },
    },
    activated() {
        this.getTreatmentHistory(this.patientId);
    },
    mounted() {
        this.getTreatmentHistory(this.patientId);
    },
    methods: {
        getScrollTop() {
            return this.$refs.historyScrollEl?.scrollTop ?? 0;
        },

        setScrollTop(scrollTop) {
            const el = this.$refs.historyScrollEl;
            if (el) {
                el.scrollTop = scrollTop;
            }
        },

        getNodeKey(item) {
            return item.dataRef?.key ?? item.key ?? null;
        },

        syncVisitedLiHighlight() {
            const scrollEl = this.$refs.historyScrollEl;
            if (!scrollEl) {
                return;
            }

            scrollEl.querySelectorAll('li.treatment-history__visited-li').forEach((li) => {
                li.classList.remove('treatment-history__visited-li');
            });

            if (!this.highlightedNodeKey) {
                return;
            }

            const marker = scrollEl.querySelector(
                `[data-history-key="${this.highlightedNodeKey}"]`,
            );
            marker?.closest('li')?.classList.add('treatment-history__visited-li');
        },

        scrollToHighlightedNode() {
            if (!this.highlightedNodeKey || !this.$refs.historyScrollEl) {
                return;
            }
            const marker = this.$refs.historyScrollEl.querySelector(
                `[data-history-key="${this.highlightedNodeKey}"]`,
            );
            marker?.closest('li')?.scrollIntoView({ block: 'nearest', behavior: 'auto' });
        },

        applyRestoreState(restoreState) {
            if (!restoreState) {
                return;
            }

            if (Array.isArray(restoreState.expandedKeys)) {
                this.expandedKeys = [...restoreState.expandedKeys];
            }

            if (typeof restoreState.lastVisitedNodeKey === 'string') {
                this.highlightedNodeKey = restoreState.lastVisitedNodeKey;
            }

            this.$nextTick(() => {
                if (typeof restoreState.scrollTop === 'number') {
                    this.setScrollTop(restoreState.scrollTop);
                }
                this.syncVisitedLiHighlight();
                this.scrollToHighlightedNode();
                this.$emit('restored');
            });
        },

        buildPageBackData(lastVisitedNodeKey = null) {
            const treatmentHistory = {
                expandedKeys: [...this.expandedKeys],
                scrollTop: this.getScrollTop(),
            };

            if (lastVisitedNodeKey) {
                treatmentHistory.lastVisitedNodeKey = lastVisitedNodeKey;
            }

            const pageData = {
                ...(this.backNavigationContext || {}),
                treatmentHistory,
            };

            if (this.sourcePageName === PAGE_NAMES.MAIN) {
                pageData.selectedPatient = this.patientId;

                if (typeof this.getPatientListScrollTop === 'function') {
                    pageData.patientList = {
                        scrollTop: this.getPatientListScrollTop(),
                        current: this.backNavigationContext?.paginationCurrent,
                    };
                }
            }

            return pageData;
        },

        getTreatmentHistory(id) {
            this.isLoading = true;
            this.historyData = [];

            if (!this.restoreState) {
                this.highlightedNodeKey = null;
            }

            if (this.historyRequestAbortSource) {
                this.historyRequestAbortSource.cancel();
            }
            this.historyRequestAbortSource = axios.CancelToken.source();

            const params = {
                doctorInChargeId: this.onlyStaffPatients ? localStorage.staffId : null,
            };
            if (id) {
                params.patientId = id;
            } else {
                params.dateTo = this.$moment().format('YYYY-MM-DD');
                params.dateFrom = this.$moment().add(-7, 'days').format('YYYY-MM-DD');
            }
            axios.get('/patient/treatment/history', {
                params,
                cancelToken: this.historyRequestAbortSource.token,
            }).then((response) => {
                this.historyData = this.prepareHistoryData(response.data);
                this.isLoading = false;
                this.$nextTick(() => {
                    if (this.restoreState) {
                        this.applyRestoreState(this.restoreState);
                    }
                });
            }).catch((err) => {
                if (axios.isCancel(err)) {
                    return;
                }
                showErrorNotification('Не удалось загрузить историю лечения.');
                this.isLoading = false;
            });
        },

        getEventDescription(entityName) {
            switch (entityName) {
            case 'SURVEYQUIZ': return {
                icon: 'profile',
                color: '#ff5118',
                path: '/poll/',
                key: 'poll',
            };
            case 'APPOINTMENTPROTOCOL': return {
                icon: 'medicine-box',
                color: '#1890ff',
                path: '/reception/',
                key: 'reception',
            };
            case 'SURVEYLABS': return {
                icon: 'experiment',
                color: '#52c41a',
                path: '/survey/',
                key: 'survey',
            };
            case 'PATIENTTASK': return {
                icon: 'phone',
                color: '#1890ff',
                path: '/reception/',
                key: 'patient-task',
            };
            default: return {
                icon: 'profile',
                color: '#ff5118',
                path: '',
                key: entityName,
            };
            }
        },

        onHistoryItemSelect(selectedKeys, { selectedNodes }) {
            if (!selectedNodes.length) {
                return;
            }
            const url = selectedNodes[0].props
                ? selectedNodes[0].props.url
                : null;
            if (!url) {
                return;
            }

            const lastVisitedNodeKey = selectedKeys[0]
                ?? selectedNodes[0]?.key
                ?? null;

            savePageBackState(
                this.$route.fullPath || '/',
                this.sourcePageName,
                this.buildPageBackData(lastVisitedNodeKey),
            );

            this.$router.push({ path: url });
        },

        prepareHistoryData(data) {
            const chargedData = data.map((rec) => ({
                ...rec,
                key: this.getEventDescription(rec.entityName.toUpperCase()).key + rec.id,
                url: this.getEventDescription(rec.entityName.toUpperCase()).path + rec.id,
            }));
            const recordsMap = chargedData.reduce((acc, rec) => {
                acc[rec.id] = rec;
                return acc;
            }, {});

            const surveysByDate = [];
            let dateGroupId = 1;
            chargedData.forEach((rec) => {
                if (rec.parentEventId) {
                    const parent = recordsMap[rec.parentEventId];
                    if (!parent.children) {
                        parent.children = [];
                    }
                    parent.children.push(rec);
                    // eslint-disable-next-line no-param-reassign
                    rec.isChild = true;
                    return;
                }
                if (rec.entityName !== 'surveyLabs') {
                    return;
                }

                const key = this.patientId
                    ? rec.eventDate
                    : `${rec.eventDate}${rec.patientName}`;
                if (!recordsMap[key]) {
                    const dateRecord = {
                        entityName: 'surveyLabs',
                        eventDate: rec.eventDate,
                        eventInfo: '',
                        eventName: rec.eventName,
                        id: `dateGroup${dateGroupId}`,
                        key: `dateGroup${dateGroupId}`,
                        parentEventId: undefined,
                        url: undefined,
                        patientName: rec.patientName,
                        children: [],
                    };
                    dateGroupId += 1;
                    surveysByDate.push(dateRecord);
                    recordsMap[key] = dateRecord;
                } else {
                    recordsMap[key].eventName = 'Введён результат исследований';
                }
                // eslint-disable-next-line no-param-reassign
                rec.parentEventId = recordsMap[key].id;
                recordsMap[key].children.push(rec);
                // eslint-disable-next-line no-param-reassign
                rec.isChild = true;
            });
            const resultData = chargedData
                .filter((rec) => !rec.parentEventId)
                .concat(surveysByDate)
                .sort((a, b) => (b.eventDate || Date()).localeCompare(a.eventDate));
            return resultData;
        },
    },
};
</script>

<style lang="scss">
.main-events-timeline {
  .treatment-history__scroll {
    overflow: auto;
    height: 100%;
    background-color: #fff;
  }

  .ant-spin-spinning {
    margin-top: 10px;
    width: 100%;
  }
  .ant-spin-container {
    height: 100%;
  }
}
.events-timeline .ant-tree li {
    white-space: inherit;
    span.ant-tree-iconEle {
        width: 16px;
    }
    .ant-tree-title {
        margin-left: 4px;
    }
}
.events-timeline .ant-tree li .ant-tree-node-content-wrapper {
    height: auto;
    display: flex;
}
.events-timeline {
    .ant-tree li span.ant-tree-switcher.ant-tree-switcher-noop {
        float: left;
    }
    .ant-tree li span.ant-tree-switcher {
        float: left;
    }

    .ant-tree li.treatment-history__visited-li {
        background-color: #d6f39f;
        border-radius: 2px;
        padding: 4px 0px;
    }
}
</style>
